// src/common/theme.ts
import { createTheme, responsiveFontSizes, alpha } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    brand?: {
      primaryLighter: string;
      primaryExtraLight: string;
      successLighter: string;
      warningLighter: string;
      errorLighter: string;
    };
  }
  interface PaletteOptions {
    brand?: {
      primaryLighter: string;
      primaryExtraLight: string;
      successLighter: string;
      warningLighter: string;
      errorLighter: string;
    };
  }
}

// ---- Palette (your colors, normalized) ----
const palette = {
  mode: 'light',
  primary: {
    dark: '#0025be',
    main: '#002abe',
    light: '#85CFED',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#0068c8',
    light: '#80B9F2',
    dark: '#0052A0',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#007cee',
    light: '#66B6F7',
    dark: '#005bb1',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#00B0A6',
    light: '#80D7D2',
    dark: '#00867F',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#ffb800',
    light: '#FFDB80',
    dark: '#C78F00',
    contrastText: '#111827',
  },
  error: {
    main: '#f5004a',
    light: '#FA80A4',
    dark: '#b00035',
    contrastText: '#FFFFFF',
  },
  text: {
    primary: '#41414d',
    secondary: '#85858d',
    disabled: '#c0c0c7',
  },
  background: {
    default: '#FAF5F5',
    paper: '#FFFFFF',
  },
  divider: '#E5E7EB',
  contrastThreshold: 3,
  tonalOffset: 0.2,

  brand: {
    primaryLighter: '#B6E2F5',
    primaryExtraLight: '#E7F5FB',
    successLighter: '#B3E7E4',
    warningLighter: '#FFEAB3',
    errorLighter: '#FCB3C9',
  },
} as const;

// helpful neutral ramp for borders/hover fills
const grey = {
  25: '#FCFCFD',
  50: '#F8FAFC',
  100: '#F2F4F7',
  200: '#EAECF0',
  300: '#D0D5DD',
  400: '#98A2B3',
  500: '#667085',
  600: '#475467',
  700: '#344054',
  800: '#1D2939',
  900: '#101828',
};

// ---- Typography ----
const typography = {
  fontFamily:
    'Inter, Montserrat, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
  h1: { fontFamily: 'Inter', fontWeight: 900, fontSize: '64px', lineHeight: 1.1, letterSpacing: '-0.02em' },
  h2: { fontFamily: 'Inter', fontWeight: 400, fontSize: '40px', lineHeight: 1.2 },
  h3: { fontFamily: 'Inter', fontWeight: 700, fontSize: '24px', lineHeight: 1.25 },
  h4: { fontFamily: 'Montserrat', fontWeight: 600, fontSize: '20px', lineHeight: 1.3 },
  h5: { fontFamily: 'Montserrat', fontWeight: 700, fontSize: '14px', lineHeight: 1.35 },
  h6: { fontFamily: 'Montserrat', fontWeight: 700, fontSize: '12px', lineHeight: 1.4 },
  body1: { fontFamily: 'Montserrat', fontWeight: 400, fontSize: '16px', lineHeight: 1.6 },
  body2: { fontFamily: 'Montserrat', fontWeight: 500, fontSize: '14px', lineHeight: 1.55 },
  button: { fontFamily: 'Montserrat', fontWeight: 700, fontSize: '14px', textTransform: 'none' },
  overline: { fontFamily: 'Inter', fontWeight: 400, fontSize: '12px', letterSpacing: '.06em', textTransform: 'uppercase' },
  caption: { fontFamily: 'Montserrat', fontWeight: 400, fontSize: '10px' },
} as const;

// ---- Base shape + shadows ----
const baseRadius = 10;
const inputRadius = 8;

// Optimized shadows: reduced blur and spread for better paint performance
const shadows: any = [
  'none',
  '0 1px 2px rgba(0,0,0,0.05)',
  '0 4px 6px 2px rgba(0,0,0,0.1), 0 2px 4px 2px rgba(0,0,0,0.06)',
  '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
  '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
  '0 25px 50px -12px rgba(0,0,0,0.25)',
  ...Array(19).fill('0 25px 50px -12px rgba(0,0,0,0.25)'),
];

// ---- Theme ----
let theme = createTheme({
  palette,
  typography,
  shape: { borderRadius: baseRadius },
  shadows,

  components: {
    // Global html/body polish via CssBaseline
    MuiCssBaseline: {
      styleOverrides: {
        html: { scrollBehavior: 'smooth' },
        body: {
          backgroundColor: palette.background.default,
          color: palette.text.primary,
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          textRendering: 'optimizeLegibility',
        },
        a: {
          transition: 'color .15s ease, text-decoration-color .15s ease',
        },
      },
    },

    // Surfaces
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ ownerState }) => ({
          borderRadius: baseRadius + 4,
          backgroundImage: 'none',
          // Only apply shadow if elevation is actually set, otherwise use border
          boxShadow: ownerState.elevation > 0 ? shadows[ownerState.elevation] : 'none',
          border: ownerState.elevation === 0 ? `1px solid ${alpha(grey[300], 0.8)}` : 'none',
        }),
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ ownerState }) => ({
          borderRadius: baseRadius + 4,
          backgroundImage: 'none',
          // Use a consistent elevation approach
          boxShadow: ownerState.elevation > 0 ? shadows[ownerState.elevation] : shadows[1],
          border: `1px solid ${alpha(grey[300], 0.9)}`,
        }),
      },
    },

    // AppBar / Toolbar
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'default' },
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${grey[200]}`,
          backgroundColor: palette.background.paper,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: { minHeight: 64, '@media (min-width:600px)': { minHeight: 72 } },
      },
    },

    // Buttons
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: baseRadius,
          fontWeight: 700,
          letterSpacing: '.01em',
          textTransform: 'none',
          // Removed box-shadow from transitions as it causes paint lag
          transition: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms linear',
          '&:hover': {
            transform: 'translateY(-1px)',
            // Use subtle shadow on hover instead of heavy animation
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
          },
          '&:active': { transform: 'translateY(0)' },
        },
        containedPrimary: {
          '&:hover': { boxShadow: shadows[2] },
        },
        outlinedPrimary: {
          borderColor: alpha(palette.primary.main, 0.45),
          '&:hover': { borderColor: palette.primary.main, backgroundColor: alpha(palette.primary.main, 0.06) },
        },
        textPrimary: {
          '&:hover': { backgroundColor: alpha(palette.primary.main, 0.06) },
        },
      },
    },

    // Inputs
    MuiTextField: {
      defaultProps: { variant: 'outlined', fullWidth: true },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: inputRadius,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(palette.primary.main, 0.45),
          },
        },
        notchedOutline: {
          borderColor: grey[300],
        },
        input: {
          '::placeholder': { color: alpha(palette.text.secondary, 0.8) },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: palette.text.secondary,
          '&.Mui-focused': { color: palette.primary.main },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { marginLeft: 0 } },
    },

    // Data display
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          border: `1px solid ${alpha(palette.primary.main, 0.3)}`,
          backgroundColor: alpha(palette.primary.main, 0.10),
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 10,
          boxShadow: shadows[1],
          fontSize: 12,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: { root: { borderRadius: baseRadius } },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 700,
          border: `2px solid ${palette.background.paper}`,
        },
      },
    },

    // Feedback
    MuiAlert: {
      defaultProps: { variant: 'standard' },
      styleOverrides: {
        root: { borderRadius: baseRadius },
        outlinedInfo: { borderColor: alpha(palette.info.main, 0.35), backgroundColor: alpha(palette.info.main, 0.06) },
        outlinedSuccess: { borderColor: alpha(palette.success.main, 0.35), backgroundColor: alpha(palette.success.main, 0.06) },
        outlinedWarning: { borderColor: alpha(palette.warning.main, 0.4), backgroundColor: alpha(palette.warning.main, 0.08) },
        outlinedError: { borderColor: alpha(palette.error.main, 0.35), backgroundColor: alpha(palette.error.main, 0.06) },
      },
    },
    MuiSnackbar: {
      styleOverrides: { root: { maxWidth: 560 } },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: baseRadius, height: 6 },
        bar: { borderRadius: baseRadius },
      },
    },

    // Navigation / selection
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          '&.Mui-selected': { color: palette.primary.main },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: baseRadius,
          border: `1px solid ${grey[200]}`,
          boxShadow: shadows[3],
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: baseRadius - 6,
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: alpha(palette.primary.main, 0.10),
          },
        },
      },
    },

    // Dialogs & sheets
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: baseRadius + 2,
          border: `1px solid ${grey[200]}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          borderRight: `1px solid ${grey[200]}`,
        },
      },
    },

    // Tables
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: baseRadius,
          border: `1px solid ${grey[200]}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: grey[50],
          '& .MuiTableCell-root': { fontWeight: 700 },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: grey[200] },
      },
    },

    // Misc
    MuiDivider: {
      styleOverrides: { root: { borderColor: grey[200] } },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': { color: palette.primary.main },
          '&.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.primary.main },
        },
        track: { backgroundColor: grey[300] },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: { backgroundColor: grey[100] },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: { color: palette.text.secondary },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: baseRadius,
          border: `1px solid ${grey[200]}`,
          '&::before': { display: 'none' },
        },
      },
    },
  },
});

// responsive type (looks great with Inter/Montserrat)
theme = responsiveFontSizes(theme, { factor: 1.8 });

export { theme };
